import {
  needsWriting,
  placeholderTokens,
} from "@/features/settings/setup/placeholders"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertTriangle } from "lucide-react"
import { useTemplateSettings } from "../../../../api/templateSettingsApi"
import type { DraftSettingsTarget } from "./DraftSettingsSheet"
import { sentSignoff } from "./SignoffPlaceholderDialog"
import type { AttachmentFormat, Lang } from "./inquiryDraftMock"

/**
 * Above the draft's preview: the org settings that would still send
 * placeholder text with this inquiry ("[Your name]"), in its language and
 * format, each with the way to fix it. The same check as the settings pages
 * and the setup checklist. Nothing once they are written.
 */
export function DraftPlaceholderNotice({
  language,
  format,
  onFix,
}: {
  language: Lang
  /** The format that actually goes out (the org's, when locked). */
  format: AttachmentFormat
  onFix: (target: DraftSettingsTarget) => void
}) {
  const { translate } = useTranslation()
  const { data: settings } = useTemplateSettings()
  if (!settings) return null

  const listed = (text: string) =>
    placeholderTokens(text).join(", ") ||
    translate("MC_INQ_DRAFT_PLACEHOLDERS_EMPTY")
  const { signature, footer } = sentSignoff(settings, language, format)
  const issues: { target: DraftSettingsTarget; text: string }[] = []
  if (needsWriting(signature))
    issues.push({
      target: "email.signature",
      text: translate("MC_INQ_DRAFT_PLACEHOLDERS_SIGNATURE", {
        language: language.toUpperCase(),
        placeholders: listed(signature),
      }),
    })
  if (footer !== null && needsWriting(footer))
    issues.push({
      target: "inquiry.footer",
      text: translate("MC_INQ_DRAFT_PLACEHOLDERS_FOOTER", {
        placeholders: listed(footer),
      }),
    })
  if (issues.length === 0) return null

  return (
    <div
      role="status"
      className="flex flex-col gap-1.5 border-b border-grey4 bg-grey2 px-4 py-2 text-[12px] text-warning"
    >
      <span className="flex items-center gap-2 font-medium">
        <AlertTriangle aria-hidden className="h-3.5 w-3.5 shrink-0" />
        {translate("MC_INQ_DRAFT_PLACEHOLDERS_TITLE")}
      </span>
      {issues.map(({ target, text }) => (
        <span key={target} className="flex flex-wrap items-center gap-x-2 pl-5">
          <span className="text-grey5">{text}</span>
          <button
            type="button"
            onClick={() => onFix(target)}
            className="font-medium text-yellow1 hover:underline"
          >
            {translate("MC_INQ_DRAFT_PLACEHOLDERS_FIX")}
          </button>
        </span>
      ))}
    </div>
  )
}
